import {
    Alert,
    AutocompleteRenderInputParams,
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import BaseDialog from '../ui/BaseDialog';
import { useAssociationState } from './provider/context';
import { useTranslation } from 'react-i18next';
import BaseAutoCompleteInput from '../ui/BaseAutoCompleteInput';
import { ProductOption } from '@/types/pages/device';
import { filterLayoutsByPairingMode, findStyleInfo } from '@/utils/associateUtils';
import { Category } from '@/types/pages/storeManagement/device';
import { useAssociationHook } from './provider/useAssociationHook';
import { isNull } from 'lodash';
import { ASSOCIATE_PAIRING_MODE } from '@/types/pages/storeManagement/device/associate';
import { useState } from 'react';

type MultiAssociateDialogProps = {
    open: boolean;
};

const MultiProfileDialog = ({ open }: MultiAssociateDialogProps) => {
    const { t } = useTranslation('common');
    const { state, dispatch } = useAssociationState();
    const { handleMultiProfileConfirm } = useAssociationHook();
    const { currentProfile, multiAssociateTemp, selectedPairingMode, noCampaignWording } = state;
    const { styleOptions, styleInfo } = state.multi;
    const [profileConfirmLoading, setProfileConfirmLoading] = useState(false);

    if (!currentProfile) return null;

    const profileKey = currentProfile.identifier;

    const { products, layoutId } = multiAssociateTemp[profileKey] || {
        products: [],
        layoutId: null,
    };

    const styleLayouts = currentProfile.subLayouts[styleInfo.slug] || [];
    const availableLayouts = filterLayoutsByPairingMode(styleLayouts, selectedPairingMode);
    const hasLayouts = availableLayouts.length > 0;

    const disableContinue = !hasLayouts || products.some((o) => o === null);

    const handleLayoutOnChange = () => {
        if (isNull(layoutId)) {
            dispatch({ type: 'SET_DIALOG', payload: { key: 'defaultLayout', open: true } });
            return;
        }
        dispatch({
            type: 'SET_MULTI_LAYOUT_RELATIONS',
            payload: null,
        });
    };

    const handleStyleOnChange = (event: SelectChangeEvent<number>) => {
        const nextStyle = findStyleInfo(event.target.value as number, styleOptions) as Category;
        dispatch({ type: 'SET_MULTI_STYLE', payload: nextStyle });
    };

    const handleSelectProductAt = (index: number, product: ProductOption | null) => {
        dispatch({ type: 'SET_MULTI_PRODUCT', payload: { product, index } });
        if (!product) {
            dispatch({ type: 'SET_NO_CAMPAIGN_WORDING', payload: '' });
        }
    };

    const handleConfirm = async () => {
        if (profileConfirmLoading) return;
        try {
            setProfileConfirmLoading(true);
            dispatch({ type: 'SET_NO_CAMPAIGN_WORDING', payload: '' });
            const barCodes = products.map((product) => (product as ProductOption).barcode);
            await handleMultiProfileConfirm(barCodes);
        } finally {
            setProfileConfirmLoading(false);
        }
    };

    return (
        <BaseDialog
            open={open}
            dialogTitle={t('Multi Associate')}
            confirmButtonText={t('Continue')}
            cancelButtonText={''}
            disableConfirm={disableContinue || profileConfirmLoading || !!noCampaignWording}
            showSpinnerOnDisable={profileConfirmLoading}
            showTopRightClose
            onConfirm={handleConfirm}
            onClose={() => {
                if (profileConfirmLoading) return;
                dispatch({ type: 'SET_NO_CAMPAIGN_WORDING', payload: '' });
                dispatch({ type: 'SET_DIALOG', payload: { key: 'multiProfile', open: false } });
            }}
        >
            <Stack
                spacing={2}
                sx={{
                    width: '100%',
                    pointerEvents: profileConfirmLoading ? 'none' : 'auto',
                    opacity: profileConfirmLoading ? 0.6 : 1,
                    userSelect: profileConfirmLoading ? 'none' : 'auto',
                }}
            >
                {/* Styles */}
                <Box>
                    <Typography fontWeight={700} sx={{ mb: 0.5 }}>
                        {t('Styles')}
                    </Typography>

                    <Stack spacing={1.25}>
                        <FormControl size="small" sx={{ width: 240 }}>
                            <Select<number> value={styleInfo.id} onChange={handleStyleOnChange} displayEmpty>
                                {styleOptions.map((o) => (
                                    <MenuItem key={o.id} value={o.id}>
                                        {o.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {hasLayouts ? (
                            <FormControlLabel
                                sx={{
                                    m: 0,
                                    display: selectedPairingMode === ASSOCIATE_PAIRING_MODE.NORMAL ? 'flex' : 'none',
                                }}
                                control={
                                    <Checkbox checked={isNull(layoutId)} onChange={handleLayoutOnChange} size="small" />
                                }
                                label={
                                    <Typography fontSize={14} sx={{ userSelect: 'none' }}>
                                        {t('Use Default Layout')}
                                    </Typography>
                                }
                            />
                        ) : (
                            <Alert severity="warning">
                                {t(
                                    'This style has no layouts. Please switch to another Column or attach layouts first.',
                                )}
                            </Alert>
                        )}
                    </Stack>
                </Box>

                {/* Products */}
                <Box sx={{ opacity: hasLayouts ? 1 : 0.55, pointerEvents: hasLayouts ? 'auto' : 'none' }}>
                    <Typography fontWeight={700}>{t('Products')}</Typography>

                    <Stack
                        spacing={1.25}
                        sx={{
                            maxHeight: 240,
                            overflowY: 'auto',
                            py: 0.5,
                        }}
                    >
                        {products.map((o, idx) => (
                            <BaseAutoCompleteInput
                                key={`${styleInfo.slug}-${idx}`}
                                selectedProduct={o}
                                setSelectedProduct={(product) => handleSelectProductAt(idx, product)}
                                renderInput={(params: AutocompleteRenderInputParams) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        placeholder={t('Search Product by Name, Barcode, or Number')}
                                        variant="outlined"
                                    />
                                )}
                            />
                        ))}
                    </Stack>

                    {!hasLayouts ? (
                        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                            {t('Products are disabled because the selected style has no layout.')}
                        </Typography>
                    ) : null}

                    {noCampaignWording ? (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                            {noCampaignWording}
                        </Alert>
                    ) : null}
                </Box>
            </Stack>
        </BaseDialog>
    );
};

export default MultiProfileDialog;
